import { createMemo, For, Show } from "solid-js";
import { keepPreviousData } from "@tanstack/solid-query";
import {
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  MapPin,
  PieChart,
  Plus,
} from "@fleet/icons";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, PageContainer, PageHeader } from "@fleet/ui";
import { BarChart, type ChartDatum, DonutChart, ProgressMeter } from "@/components/ui/charts";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { msg } from "@lingui/core/macro";
import { createPowerSyncQuery } from "@fieldcheck/shared/solid";
import {
  cn,
  FINDING_SEVERITIES,
  type FindingSeverity,
  type FindingWithMeta,
  getFindingsWithMetaQuery,
  getInspectionStatusCountsQuery,
  getInspectionsWithMetaQuery,
  getOpenFindingCountsQuery,
  type InspectionWithMeta,
  isFindingResolved,
  isFindingSeverity,
  queryKeys,
} from "@fieldcheck/shared";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";
import { SEVERITY_LABELS } from "@/features/findings/components/finding-dialog";
import { STATUS_LABELS } from "@/features/inspections/components/inspections-view";
import { Link, useNavigate } from "@tanstack/solid-router";
import { useActiveContext } from "@/features/organizations/hooks/use-active-context";

const RECENT_LIMIT = 5;
const TOP_SITES_LIMIT = 6;
const APPROVAL_HEALTHY = 80;

const SEVERITY_COLORS: Record<FindingSeverity, string> = {
  low: "text-muted-foreground",
  medium: "text-warning",
  high: "text-danger",
  critical: "text-danger",
};

/** Tailwind background tokens for severity bars (bar charts use bg-*). */
const SEVERITY_BAR_COLORS: Record<FindingSeverity, string> = {
  low: "bg-muted-foreground",
  medium: "bg-warning",
  high: "bg-danger",
  critical: "bg-danger",
};

export function Dashboard() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const navigate = useNavigate();
  const { userId } = session;
  const activeCtx = useActiveContext();

  const openFindingCounts = createPowerSyncQuery<{ severity: string | null; count: number }>({
    queryKey: () => queryKeys.findings.openStats(userId(), activeCtx.activeOrgId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance
        ? getOpenFindingCountsQuery(dbInstance, { scope: activeCtx.scope() })
        : null;
    },
    enabled: () => Boolean(drizzle()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    placeholderData: keepPreviousData,
    suspense: false,
  });

  const inspectionCounts = createPowerSyncQuery<{ status: string | null; count: number }>({
    queryKey: () => queryKeys.inspections.stats(userId(), activeCtx.activeOrgId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance
        ? getInspectionStatusCountsQuery(dbInstance, { scope: activeCtx.scope() })
        : null;
    },
    enabled: () => Boolean(drizzle()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    placeholderData: keepPreviousData,
    suspense: false,
  });

  const inspections = createPowerSyncQuery<InspectionWithMeta>({
    queryKey: () => queryKeys.inspections.list(userId(), undefined, activeCtx.activeOrgId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance
        ? getInspectionsWithMetaQuery(dbInstance, { scope: activeCtx.scope() })
        : null;
    },
    enabled: () => Boolean(drizzle()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    placeholderData: keepPreviousData,
    suspense: false,
  });

  const findings = createPowerSyncQuery<FindingWithMeta>({
    queryKey: () =>
      queryKeys.findings.list({ userId: userId() ?? null, scope: activeCtx.activeOrgId() }),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getFindingsWithMetaQuery(dbInstance, { scope: activeCtx.scope() }) : null;
    },
    enabled: () => Boolean(drizzle()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    placeholderData: keepPreviousData,
    suspense: false,
  });

  const severityCount = (sev: FindingSeverity) =>
    (openFindingCounts.data ?? []).find((r) => r.severity === sev)?.count ?? 0;

  const totalOpen = createMemo(() => {
    let total = 0;
    for (const row of openFindingCounts.data ?? []) {
      total += row.count;
    }
    return total;
  });

  const statusCount = (status: string) =>
    (inspectionCounts.data ?? []).find((r) => r.status === status)?.count ?? 0;

  const recentInspections = createMemo(() => (inspections.data ?? []).slice(0, RECENT_LIMIT));
  const openFindings = createMemo(() =>
    (findings.data ?? []).filter((f) => !isFindingResolved(f.status)).slice(0, RECENT_LIMIT),
  );

  // ── Analytics ──────────────────────────────────────────────────────────────

  /** Open findings by severity (bar chart). Drills into /findings?severity=… */
  const findingsBySeverity = createMemo<ChartDatum[]>(() =>
    [...FINDING_SEVERITIES].map((sev) => ({
      key: sev,
      label: t(SEVERITY_LABELS[sev]),
      value: severityCount(sev),
      colorClass: SEVERITY_BAR_COLORS[sev],
      onSelect: () => void navigate({ to: "/findings", search: { severity: sev, status: "open" } }),
    })),
  );

  /** Inspections by status (donut). */
  const inspectionsByStatus = createMemo<ChartDatum[]>(() => [
    {
      key: "in_progress",
      label: t(STATUS_LABELS.in_progress),
      value: statusCount("in_progress"),
      colorClass: "text-warning",
    },
    {
      key: "completed",
      label: t(STATUS_LABELS.completed),
      value: statusCount("completed"),
      colorClass: "text-success",
    },
    {
      key: "draft",
      label: t(STATUS_LABELS.draft),
      value: statusCount("draft"),
      colorClass: "text-muted-foreground",
    },
  ]);

  const totalInspections = createMemo(() =>
    inspectionsByStatus().reduce((sum, d) => sum + d.value, 0),
  );

  /** Inspections grouped by site (top sites, bar chart). */
  const inspectionsBySite = createMemo<ChartDatum[]>(() => {
    const counts = new Map<string, { label: string; value: number; siteId: string | null }>();
    for (const insp of inspections.data ?? []) {
      const key = insp.site_id ?? "__none__";
      const label = insp.site_name ?? t(msg`No site`);
      const existing = counts.get(key);
      if (existing) {
        existing.value += 1;
      } else {
        counts.set(key, { label, value: 1, siteId: insp.site_id });
      }
    }
    return [...counts.values()]
      .toSorted((a, b) => b.value - a.value)
      .slice(0, TOP_SITES_LIMIT)
      .map((c) => ({
        key: c.siteId ?? "__none__",
        label: c.label,
        value: c.value,
        colorClass: "bg-primary",
        onSelect: c.siteId
          ? () => void navigate({ to: "/inspections", search: { siteId: c.siteId ?? undefined } })
          : undefined,
      }));
  });

  /** Approval rate: completed inspections with no open findings vs. with findings. */
  const approvalRate = createMemo(() => {
    const completed = inspections.data?.filter((i) => i.status === "completed") ?? [];
    if (completed.length === 0) {
      return null;
    }
    const inspectionsWithOpenFinding = new Set(
      (findings.data ?? [])
        .filter((f) => !isFindingResolved(f.status) && f.inspection_id)
        .map((f) => f.inspection_id),
    );
    const passed = completed.filter((i) => !inspectionsWithOpenFinding.has(i.id)).length;
    return Math.round((passed / completed.length) * 100);
  });

  return (
    <PageContainer maxWidth="5xl" class="overflow-y-auto">
      <div class="flex items-center justify-between gap-4 mb-6">
        <PageHeader title={t(msg`Dashboard`)} description={t(msg`Field operations at a glance`)} />
        <Button
          class="gap-2 shrink-0"
          data-testid="dashboard-new-inspection"
          onClick={() => void navigate({ to: "/inspections" })}
        >
          <Plus class="h-4 w-4" />
          {t(msg`New inspection`)}
        </Button>
      </div>

      {/* Hallazgos abiertos por severidad */}
      <div class="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="open-findings-stats">
        <For each={[...FINDING_SEVERITIES]}>
          {(sev) => (
            <Card>
              <CardContent class="flex flex-col items-center justify-center p-4 text-center">
                <span class={cn("text-3xl font-black", SEVERITY_COLORS[sev])}>
                  {severityCount(sev)}
                </span>
                <span class="mt-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  {t(SEVERITY_LABELS[sev])}
                </span>
              </CardContent>
            </Card>
          )}
        </For>
      </div>

      <div class="mt-3 grid grid-cols-3 gap-3">
        <Card>
          <CardContent class="flex flex-col items-center p-4">
            <span class="text-2xl font-black text-warning">{statusCount("in_progress")}</span>
            <span class="mt-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              {t(STATUS_LABELS.in_progress)}
            </span>
          </CardContent>
        </Card>
        <Card>
          <CardContent class="flex flex-col items-center p-4">
            <span class="text-2xl font-black text-success">{statusCount("completed")}</span>
            <span class="mt-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              {t(STATUS_LABELS.completed)}
            </span>
          </CardContent>
        </Card>
        <Card>
          <CardContent class="flex flex-col items-center p-4">
            <span class="text-2xl font-black text-foreground">{totalOpen()}</span>
            <span class="mt-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              {t(msg`Open findings`)}
            </span>
          </CardContent>
        </Card>
      </div>

      {/* ── Analítica: gráficos theme-aware ─────────────────────────── */}
      <div class="mt-6 grid gap-6 lg:grid-cols-2">
        {/* Hallazgos abiertos por severidad (barras) */}
        <Card>
          <CardHeader>
            <div class="flex items-center gap-2">
              <BarChart3 class="h-5 w-5 text-primary" />
              <CardTitle>{t(msg`Open findings by severity`)}</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <BarChart
              title={t(msg`Open findings by severity`)}
              caption={t(msg`Number of open findings grouped by severity`)}
              data={findingsBySeverity()}
              emptyHint={
                <p class="py-6 text-center text-sm text-muted-foreground">
                  {t(msg`No open findings to chart.`)}
                </p>
              }
            />
          </CardContent>
        </Card>

        {/* Inspecciones por estado (donut) */}
        <Card>
          <CardHeader>
            <div class="flex items-center gap-2">
              <PieChart class="h-5 w-5 text-primary" />
              <CardTitle>{t(msg`Inspections by status`)}</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <DonutChart
              title={t(msg`Inspections by status`)}
              caption={t(msg`Distribution of inspections across statuses`)}
              data={inspectionsByStatus()}
              centerValue={totalInspections()}
              centerLabel={t(msg`Total`)}
              emptyHint={
                <p class="py-6 text-center text-sm text-muted-foreground">
                  {t(msg`No inspections to chart yet.`)}
                </p>
              }
            />
          </CardContent>
        </Card>

        {/* Inspecciones por sitio (barras, top) */}
        <Card>
          <CardHeader>
            <div class="flex items-center gap-2">
              <MapPin class="h-5 w-5 text-primary" />
              <CardTitle>{t(msg`Inspections by site`)}</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <BarChart
              title={t(msg`Inspections by site`)}
              caption={t(msg`Top sites by number of inspections`)}
              data={inspectionsBySite()}
              emptyHint={
                <p class="py-6 text-center text-sm text-muted-foreground">
                  {t(msg`Assign inspections to sites to see this breakdown.`)}
                </p>
              }
            />
          </CardContent>
        </Card>

        {/* Tasa de aprobación */}
        <Card>
          <CardHeader>
            <div class="flex items-center gap-2">
              <CheckCircle2 class="h-5 w-5 text-success" />
              <CardTitle>{t(msg`Approval rate`)}</CardTitle>
            </div>
          </CardHeader>
          <CardContent class="flex flex-col justify-center gap-2">
            <Show
              when={approvalRate() !== null}
              fallback={
                <p class="py-6 text-center text-sm text-muted-foreground">
                  {t(msg`Complete an inspection to measure your approval rate.`)}
                </p>
              }
            >
              <ProgressMeter
                percentage={approvalRate() ?? 0}
                label={t(msg`Completed inspections with no open findings`)}
                colorClass={(approvalRate() ?? 0) >= APPROVAL_HEALTHY ? "bg-success" : "bg-warning"}
              />
              <p class="mt-1 text-xs text-muted-foreground">
                {t(msg`Share of completed inspections that closed clean.`)}
              </p>
            </Show>
          </CardContent>
        </Card>
      </div>

      <div class="mt-6 grid gap-6 lg:grid-cols-2">
        {/* Hallazgos abiertos recientes */}
        <Card>
          <CardHeader>
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <AlertTriangle class="h-5 w-5 text-warning" />
                <CardTitle>{t(msg`Open findings`)}</CardTitle>
              </div>
              <Link
                to="/findings"
                class="flex items-center gap-1 text-xs font-bold text-primary hover:underline"
              >
                {t(msg`View all`)}
                <ChevronRight class="h-3.5 w-3.5" />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <Show
              when={openFindings().length > 0}
              fallback={
                <p class="py-6 text-center text-sm text-muted-foreground">
                  {t(msg`No open findings. Field is clean.`)}
                </p>
              }
            >
              <ul class="divide-y divide-border/40">
                <For each={openFindings()}>
                  {(f) => (
                    <li class="flex items-center gap-3 py-2.5">
                      <span
                        class={cn(
                          "text-[10px] font-black uppercase",
                          SEVERITY_COLORS[isFindingSeverity(f.severity) ? f.severity : "medium"],
                        )}
                      >
                        {isFindingSeverity(f.severity)
                          ? t(SEVERITY_LABELS[f.severity])
                          : f.severity}
                      </span>
                      <span class="min-w-0 flex-1 truncate text-sm font-medium">{f.title}</span>
                      <Show when={f.site_name}>
                        <span class="flex items-center gap-1 text-xs text-muted-foreground">
                          <MapPin class="h-3 w-3" />
                          {f.site_name}
                        </span>
                      </Show>
                    </li>
                  )}
                </For>
              </ul>
            </Show>
          </CardContent>
        </Card>

        {/* Inspecciones recientes */}
        <Card>
          <CardHeader>
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <ClipboardCheck class="h-5 w-5 text-primary" />
                <CardTitle>{t(msg`Recent inspections`)}</CardTitle>
              </div>
              <Link
                to="/inspections"
                class="flex items-center gap-1 text-xs font-bold text-primary hover:underline"
              >
                {t(msg`View all`)}
                <ChevronRight class="h-3.5 w-3.5" />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <Show
              when={recentInspections().length > 0}
              fallback={
                <p class="py-6 text-center text-sm text-muted-foreground">
                  {t(msg`No inspections yet. Create a template and run your first one.`)}
                </p>
              }
            >
              <ul class="divide-y divide-border/40">
                <For each={recentInspections()}>
                  {(insp) => (
                    <li>
                      <Link
                        to="/inspections/$inspectionId"
                        params={{ inspectionId: insp.id }}
                        class="flex items-center gap-3 py-2.5 hover:bg-accent/5 rounded-md px-1 -mx-1"
                      >
                        <Show
                          when={insp.status === "completed"}
                          fallback={<ClipboardCheck class="h-4 w-4 shrink-0 text-warning" />}
                        >
                          <CheckCircle2 class="h-4 w-4 shrink-0 text-success" />
                        </Show>
                        <span class="min-w-0 flex-1 truncate text-sm font-medium">
                          {insp.template_name ?? t(msg`Inspection`)}
                        </span>
                        <Show when={insp.site_name}>
                          <span class="flex items-center gap-1 text-xs text-muted-foreground">
                            <MapPin class="h-3 w-3" />
                            {insp.site_name}
                          </span>
                        </Show>
                      </Link>
                    </li>
                  )}
                </For>
              </ul>
            </Show>
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}
